import { Alert, CircularProgress, Stack } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { flussApi } from "modules/fluss/services/fluss-api";
import { resolveDisplayText } from "modules/record/shared/services/recordMetadataLabels";
import { FlussElementSelection } from "modules/fluss/types/fluss";
import {
    BusinessRuleTaskNodePanel,
    ManualTaskNodePanel,
    ServiceTaskNodePanel,
    ScriptTaskNodePanel,
    UserTaskNodePanel,
} from "./node-properties/ActivityNodePanels";
import CallActivityNodePanel from "./node-properties/CallActivityNodePanel";
import { DataObjectReferenceNodePanel, DataStoreReferenceNodePanel } from "./node-properties/DataReferenceNodePanels";
import SendTaskNodePanel from "./node-properties/SendTaskNodePanel";
import { EndNodePanel, ErrorEndNodePanel } from "./node-properties/EndNodePanels";
import { EscalationNodePanel } from "./node-properties/EscalationNodePanels";
import {
    BoundaryConditionalNodePanel,
    BoundaryErrorNodePanel,
    BoundarySignalNodePanel,
    BoundaryTimerNodePanel,
    MessageStartNodePanel,
    MessageCatchNodePanel,
    SignalNodePanel,
    TimerNodePanel,
} from "./node-properties/EventNodePanels";
import {
    EventBasedGatewayNodePanel,
    ExclusiveGatewayNodePanel,
    InclusiveGatewayNodePanel,
    ParallelGatewayNodePanel,
} from "./node-properties/GatewayNodePanels";
import LinkNodePanel from "./node-properties/LinkNodePanels";
import StartNodePanel from "./node-properties/StartNodePanel";
import FlussNodePanelShell from "./node-properties/FlussNodePanelShell";
import EventStartInterruptControl from "./node-properties/EventStartInterruptControl";
import {
    EventSubprocessNodePanel,
    SubprocessNodePanel,
} from "./node-properties/SubprocessNodePanels";
import {
    FlussMetadataOption,
    FlussNodePanelData,
    referenceId,
} from "./node-properties/fluss-node-panel-data";
import { panelNodeKindFor } from "./node-properties/fluss-node-panel-utils";
import {
    listManualTriggerBusinessRules,
    listFlussMetadata,
} from "./node-properties/useFlussNodePanelMetadata";
import { DataCatalogProvider } from "./data-picker/DataCatalogContext";
import { useDataCatalog } from "./node-properties/useDataCatalog";

export const FLUSS_NODE_PANEL_FEATURE_PROPERTY = ["fluss", "ui", "node_properties_panel_v2"].join(".");

export const useFlussNodePanelFeatureEnabled = (enabled = true) => {
    const query = useQuery({
        queryKey: ["fluss", "metadata", "node-panel-feature"],
        queryFn: () => listFlussMetadata("sys_properties"),
        enabled,
    });
    const property = query.data?.find((candidate) => candidate.name === FLUSS_NODE_PANEL_FEATURE_PROPERTY);
    return property ? !["false", "0", "off", "legacy"].includes(String(property.value).toLowerCase()) : true;
};

const queueOptions = (
    roles: FlussMetadataOption[],
    groups: FlussMetadataOption[],
    memberships: FlussMetadataOption[],
    text: (raw: string) => string = (raw) => raw
) => roles
    .filter((role) => role.active !== false)
    .map((role) => {
        const membership = memberships.find((candidate) => referenceId(candidate.sys_role_id) === role.id);
        const group = groups.find((candidate) => candidate.active !== false && candidate.id === referenceId(membership?.sys_group_id));
        return {
            roleName: role.name,
            groupName: text(group?.label || group?.name || role.name),
            label: text(role.label || role.name),
        };
    });

const FlussNodePropertiesPanelContent = ({
    selection,
    modeler,
    definitionKey,
    onChanged,
    canViewAdvanced,
    readOnly = false,
}: {
    selection: FlussElementSelection | null;
    modeler: any;
    definitionKey: string;
    onChanged: () => void;
    canViewAdvanced: boolean;
    readOnly?: boolean;
}) => {
    const { t } = useTranslation();
    // `onChanged` fires on every edit made through the shared bpmn-js model; the catalog hook
    // listens to `commandStack.changed` itself, so no synthetic re-render counter is needed here.
    const handleChanged = onChanged;
    const tablesQuery = useQuery({
        queryKey: ["fluss", "node-panel", "tables"],
        queryFn: () => listFlussMetadata("sys_tables"),
    });
    const rulesQuery = useQuery({
        queryKey: ["fluss", "node-panel", "business-rules"],
        queryFn: () => listFlussMetadata("sys_business_rules"),
    });
    const rolesQuery = useQuery({
        queryKey: ["fluss", "node-panel", "roles"],
        queryFn: () => listFlussMetadata("sys_roles"),
    });
    const groupsQuery = useQuery({
        queryKey: ["fluss", "node-panel", "groups"],
        queryFn: () => listFlussMetadata("sys_groups"),
    });
    const groupRolesQuery = useQuery({
        queryKey: ["fluss", "node-panel", "group-roles"],
        queryFn: () => listFlussMetadata("sys_group_roles"),
    });
    const templatesQuery = useQuery({
        queryKey: ["fluss", "node-panel", "templates"],
        queryFn: () => listFlussMetadata("sys_email_templates"),
    });
    const eventTypesQuery = useQuery({
        queryKey: ["fluss", "node-panel", "event-types"],
        queryFn: () => listFlussMetadata("sys_event_types"),
    });
    const serviceFormsQuery = useQuery({
        queryKey: ["fluss", "node-panel", "service-forms"],
        queryFn: () => listFlussMetadata("sys_sp_form_catalog"),
    });
    const factoryActionsQuery = useQuery({
        queryKey: ["fluss", "node-panel", "factory-actions"],
        queryFn: () => listFlussMetadata("sys_fluss_actions"),
    });
    const apiSecretsQuery = useQuery({
        queryKey: ["fluss", "node-panel", "api-secrets"],
        queryFn: () => listFlussMetadata("sys_api_secrets"),
    });
    const schedulesQuery = useQuery({
        queryKey: ["fluss", "node-panel", "schedules"],
        queryFn: () => listFlussMetadata("schedule"),
    });
    const manualTriggerRulesQuery = useQuery({
        queryKey: ["fluss", "node-panel", "manual-trigger-rules", definitionKey, selection?.id],
        queryFn: () => listManualTriggerBusinessRules(definitionKey, selection?.id || ""),
        enabled: Boolean(definitionKey && selection && panelNodeKindFor(selection) === "start"),
    });
    const definitionsQuery = useQuery({
        queryKey: ["fluss", "node-panel", "definitions"],
        queryFn: () => flussApi.listDefinitions(),
        enabled: Boolean(selection && panelNodeKindFor(selection) === "call_activity"),
    });
    const loading = [
        tablesQuery,
        rulesQuery,
        rolesQuery,
        groupsQuery,
        groupRolesQuery,
        templatesQuery,
        eventTypesQuery,
        factoryActionsQuery,
        apiSecretsQuery,
        schedulesQuery,
        serviceFormsQuery,
        manualTriggerRulesQuery,
        definitionsQuery,
    ].some((query) => query.isLoading);

    const data: FlussNodePanelData = useMemo(() => ({
        tables: tablesQuery.data || [],
        businessRules: rulesQuery.data || [],
        manualTriggerBusinessRules: manualTriggerRulesQuery.data || [],
        roles: rolesQuery.data || [],
        groups: groupsQuery.data || [],
        groupRoles: groupRolesQuery.data || [],
        queues: queueOptions(
            rolesQuery.data || [],
            groupsQuery.data || [],
            groupRolesQuery.data || [],
            (raw) => String(resolveDisplayText(t, raw))
        ),
        templates: templatesQuery.data || [],
        reloadTemplates: templatesQuery.refetch,
        eventTypes: eventTypesQuery.data || [],
        serviceForms: serviceFormsQuery.data || [],
        factoryActions: factoryActionsQuery.data || [],
        apiSecrets: apiSecretsQuery.data || [],
        schedules: schedulesQuery.data || [],
        definitions: definitionsQuery.data || [],
    }), [
        eventTypesQuery.data,
        factoryActionsQuery.data,
        apiSecretsQuery.data,
        schedulesQuery.data,
        groupRolesQuery.data,
        groupsQuery.data,
        rolesQuery.data,
        rulesQuery.data,
        manualTriggerRulesQuery.data,
        serviceFormsQuery.data,
        tablesQuery.data,
        templatesQuery.data,
        templatesQuery.refetch,
        definitionsQuery.data,
        t,
    ]);

    if (!selection) {
        return (
            <Alert severity="info">
                {t("fluss.node_panel.select", {
                    defaultValue: "Select a node on the canvas to configure its properties.",
                })}
            </Alert>
        );
    }

    if (loading) {
        return <Stack alignItems="center" py={3}><CircularProgress size={24} /></Stack>;
    }

    const kind = panelNodeKindFor(selection);
    const common = {
        selection,
        modeler,
        data,
        canViewAdvanced,
        readOnly,
        onChanged: handleChanged,
    };
    const eventStartPanel = (panel: ReactNode) => (
        <Stack spacing={1}>
            <EventStartInterruptControl selection={selection} modeler={modeler} readOnly={readOnly} onChanged={handleChanged} />
            {panel}
        </Stack>
    );

    if (kind === "message_start") {
        const panel = <MessageStartNodePanel {...common} />;
        return selection.businessObject?.$parent?.triggeredByEvent ? eventStartPanel(panel) : panel;
    }
    if (kind === "message_catch") return <MessageCatchNodePanel {...common} />;
    if (kind === "start") {
        if (selection.element?.parent?.type === "bpmn:SubProcess") {
            return <Alert severity="info">{t("fluss.node_panel.start.internal", { defaultValue: "This start belongs to the sub-process; its trigger is the incoming process flow." })}</Alert>;
        }
        return <StartNodePanel selection={selection} modeler={modeler} data={data} definitionKey={definitionKey} canViewAdvanced={canViewAdvanced} readOnly={readOnly} onChanged={handleChanged} />;
    }
    if (kind === "user_task" || kind === "approval") return <UserTaskNodePanel {...common} />;
    if (kind === "business_rule_task") return <BusinessRuleTaskNodePanel {...common} />;
    if (kind === "manual_task") return <ManualTaskNodePanel {...common} />;
    if (kind === "service_task") return <ServiceTaskNodePanel {...common} />;
    if (kind === "script_task") return <ScriptTaskNodePanel {...common} />;
    if (kind === "send_task") return <SendTaskNodePanel {...common} />;
    if (kind === "data_store_reference") return <DataStoreReferenceNodePanel {...common} />;
    if (kind === "data_object_reference") return <DataObjectReferenceNodePanel {...common} />;
    if (kind === "event_start_signal") return eventStartPanel(<SignalNodePanel {...common} />);
    if (kind === "event_start_timer") return eventStartPanel(<TimerNodePanel {...common} />);
    if (kind.startsWith("signal_")) return <SignalNodePanel {...common} />;
    if (kind.startsWith("timer_")) return <TimerNodePanel {...common} />;
    if (kind === "boundary_timer") return <BoundaryTimerNodePanel {...common} />;
    if (kind === "boundary_error" || kind === "event_start_error") return <BoundaryErrorNodePanel {...common} />;
    if (kind === "event_start_conditional") return eventStartPanel(<BoundaryConditionalNodePanel {...common} />);
    if (kind === "boundary_conditional") return <BoundaryConditionalNodePanel {...common} />;
    if (kind === "conditional_catch") return <BoundaryConditionalNodePanel {...common} />;
    if (kind === "boundary_signal") return <BoundarySignalNodePanel {...common} />;
    if (kind === "subprocess") return <SubprocessNodePanel {...common} />;
    if (kind === "event_subprocess") return <EventSubprocessNodePanel {...common} />;
    if (kind === "call_activity") return <CallActivityNodePanel {...common} />;
    if (kind === "exclusive_gateway" || kind === "inclusive_gateway") {
        const GatewayPanel = kind === "inclusive_gateway" ? InclusiveGatewayNodePanel : ExclusiveGatewayNodePanel;
        return (
            <GatewayPanel
                selection={selection}
                modeler={modeler}
                canViewAdvanced={canViewAdvanced}
                readOnly={readOnly}
                onChanged={handleChanged}
            />
        );
    }
    if (kind === "parallel_gateway") {
        return <ParallelGatewayNodePanel selection={selection} canViewAdvanced={canViewAdvanced} />;
    }
    if (kind === "event_based_gateway") {
        return <EventBasedGatewayNodePanel selection={selection} canViewAdvanced={canViewAdvanced} />;
    }
    if (kind === "error_end") return <ErrorEndNodePanel {...common} />;
    if (kind === "event_start_escalation") return eventStartPanel(<EscalationNodePanel {...common} />);
    if (kind === "escalation_throw" || kind === "boundary_escalation") return <EscalationNodePanel {...common} />;
    if (kind === "link_throw" || kind === "link_catch") return <LinkNodePanel {...common} />;
    if (["end", "terminate_end", "milestone"].includes(kind)) return <EndNodePanel {...common} />;

    return <FlussNodePanelShell selection={selection} kind={kind} canViewAdvanced={canViewAdvanced} tabs={[{
        key: "info",
        label: t("fluss.node_panel.tab.info", { defaultValue: "Info" }),
        content: <Alert severity="info">{t("fluss.node_panel.unsupported", {
            defaultValue: "This node type has no property panel in this phase.",
        })}</Alert>,
    }]} />;
};

// The catalog (C7) is fetched once for the selected node and shared by every DataPicker below.
const FlussNodePropertiesPanel = (props: {
    selection: FlussElementSelection | null;
    modeler: any;
    definitionKey: string;
    onChanged: () => void;
    canViewAdvanced: boolean;
    readOnly?: boolean;
}) => {
    const catalog = useDataCatalog(props.modeler, props.selection?.id, props.definitionKey);
    return (
        <DataCatalogProvider value={catalog}>
            <FlussNodePropertiesPanelContent {...props} />
        </DataCatalogProvider>
    );
};

export default FlussNodePropertiesPanel;
